import { Capacitor } from "@capacitor/core";
import {
  ActionPerformed,
  Channel,
  PushNotifications,
  PushNotificationSchema,
  Token,
} from "@capacitor/push-notifications";
import { router } from "../main";

// Track initialization state
let isInitialized = false;
let currentToken: string | null = null;

/**
 * Check if we're running on a native platform (Android/iOS)
 */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Get the current platform
 */
export function getPlatform(): string {
  return Capacitor.getPlatform();
}

/**
 * Initialize Capacitor push notifications for native platforms.
 * This should be called once when the user is authenticated.
 */
export async function initializeCapacitorPush(
  onTokenReceived: (token: string) => Promise<void>,
  onNotificationTapped?: (notification: PushNotificationSchema) => void,
): Promise<boolean> {
  if (!isNativePlatform()) {
    console.log(
      "Not on native platform, skipping Capacitor push initialization",
    );
    return false;
  }

  if (isInitialized) {
    console.log("Capacitor push already initialized");
    // If we have a token, re-send it in case registration is needed
    if (currentToken) {
      await onTokenReceived(currentToken);
    }
    return true;
  }

  try {
    // Create notification channel first (Android only, must be done before registration)
    await createNotificationChannel();

    // Check current permission status
    const permStatus = await PushNotifications.checkPermissions();
    console.log("Push notification permission status:", permStatus.receive);

    if (permStatus.receive === "prompt") {
      // Request permission
      const requestResult = await PushNotifications.requestPermissions();
      if (requestResult.receive !== "granted") {
        console.warn("Push notification permission denied");
        return false;
      }
    } else if (permStatus.receive !== "granted") {
      console.warn(
        "Push notification permission not granted:",
        permStatus.receive,
      );
      return false;
    }

    // Set up listeners before registration
    setupPushListeners(onTokenReceived, onNotificationTapped);

    // Register with FCM/APNs
    await PushNotifications.register();

    isInitialized = true;
    console.log("Capacitor push notifications initialized successfully");
    return true;
  } catch (error) {
    console.error("Failed to initialize Capacitor push notifications:", error);
    return false;
  }
}

/**
 * Set up push notification listeners
 */
function setupPushListeners(
  onTokenReceived: (token: string) => Promise<void>,
  onNotificationTapped?: (notification: PushNotificationSchema) => void,
): void {
  // Handle registration success - FCM token received
  PushNotifications.addListener("registration", async (token: Token) => {
    console.log("FCM registration token received:", token.value);
    currentToken = token.value;
    try {
      await onTokenReceived(token.value);
    } catch (error) {
      console.error("Failed to handle FCM token:", error);
    }
  });

  // Handle registration error
  PushNotifications.addListener("registrationError", (error) => {
    console.error("FCM registration error:", error);
  });

  // Handle push notification received while app is in foreground
  PushNotifications.addListener(
    "pushNotificationReceived",
    (notification: PushNotificationSchema) => {
      console.log("Push notification received in foreground:", notification);
      // This is just for logging/debugging
    },
  );

  // Handle notification tap (when user clicks the notification)
  PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (action: ActionPerformed) => {
      console.log("Push notification action performed:", action);

      const notification = action.notification;
      if (onNotificationTapped) {
        onNotificationTapped(notification);
      }

      router.navigate({
        to: "/orders",
      });
    },
  );
}

/**
 * Remove all push notification listeners
 */
export async function removeCapacitorPushListeners(): Promise<void> {
  if (!isNativePlatform()) return;

  try {
    await PushNotifications.removeAllListeners();
    isInitialized = false;
    currentToken = null;
    console.log("Capacitor push listeners removed");
  } catch (error) {
    console.error("Failed to remove push listeners:", error);
  }
}

/**
 * Get the current FCM token
 */
export function getCurrentFCMToken(): string | null {
  return currentToken;
}

/**
 * Check if push notifications are available on this platform
 */
export async function checkPushAvailability(): Promise<{
  isAvailable: boolean;
  permissionStatus: "granted" | "denied" | "prompt" | "unknown";
}> {
  if (!isNativePlatform()) {
    return { isAvailable: false, permissionStatus: "unknown" };
  }

  try {
    const permStatus = await PushNotifications.checkPermissions();
    return {
      isAvailable: true,
      permissionStatus: permStatus.receive as "granted" | "denied" | "prompt",
    };
  } catch (error) {
    console.error("Failed to check push availability:", error);
    return { isAvailable: false, permissionStatus: "unknown" };
  }
}

/**
 * Create notification channel for Android with custom sound
 * This must be called before FCM messages arrive to ensure custom sound works
 */
export async function createNotificationChannel(): Promise<void> {
  if (!isNativePlatform() || getPlatform() !== "android") {
    return;
  }

  try {
    const channel: Channel = {
      id: "order_notifications",
      name: "Order Notifications",
      description: "Notifications for order updates",
      sound: "aitisal_order_notification", // References res/raw/aitisal_order_notification.mp3
      importance: 5, // IMPORTANCE_HIGH
      visibility: 1, // VISIBILITY_PUBLIC
      lights: true,
      lightColor: "#FF0000",
      vibration: true,
    };

    await PushNotifications.createChannel(channel);
    console.log("Notification channel created successfully");
  } catch (error) {
    console.error("Failed to create notification channel:", error);
  }
}
